import { useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Tone } from '../lib/format';
import './ui.css';

// --- Button ---------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  block,
  children,
  className = '',
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`btn btn--${variant} btn--${size} ${block ? 'btn--block' : ''} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

// --- Pill -----------------------------------------------------------------

export function Pill({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`pill pill--${tone}`} title={title}>
      {children}
    </span>
  );
}

// --- Card -----------------------------------------------------------------

export function Card({
  children,
  className = '',
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={`card ${padded ? 'card--padded' : ''} ${className}`}>
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  count,
  meta,
  action,
}: {
  title: string;
  count?: number | string;
  meta?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="card__header">
      <h2 className="card__title">{title}</h2>
      {count !== undefined && <span className="card__count">{count}</span>}
      {meta && <span className="card__meta">{meta}</span>}
      <span className="spacer" />
      {action}
    </div>
  );
}

// --- Modal ----------------------------------------------------------------

/**
 * A dialog, built on the native <dialog> element.
 *
 * `showModal()` brings Escape to close, focus moved into the dialog and
 * trapped there, the rest of the page marked inert, and a real backdrop —
 * all things a div-with-a-fixed-position has to reimplement badly.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="modal"
      onClose={onClose}
      // A click that lands on the dialog itself rather than its contents is a
      // click on the backdrop.
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal__inner">
        <div className="modal__head">
          <div>
            <h2 className="modal__title">{title}</h2>
            {description && <p className="modal__description">{description}</p>}
          </div>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            {Icon.close({ size: 15 })}
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

// --- Stat tile ------------------------------------------------------------

export function StatTile({
  label,
  value,
  badge,
  badgeTone = 'neutral',
  footnote,
}: {
  label: string;
  value: ReactNode;
  badge?: ReactNode;
  badgeTone?: Tone;
  footnote?: ReactNode;
}) {
  return (
    <div className="tile">
      <div className="tile__label">{label}</div>
      <div className="tile__value-row">
        <span className="tile__value">{value}</span>
        {badge && <Pill tone={badgeTone}>{badge}</Pill>}
      </div>
      {footnote && <div className="tile__footnote">{footnote}</div>}
    </div>
  );
}

// --- Avatar ---------------------------------------------------------------

export function Avatar({
  initials,
  size = 28,
  tone = 'accent',
}: {
  initials: string;
  size?: number;
  tone?: 'accent' | 'muted';
}) {
  return (
    <span
      className={`avatar avatar--${tone}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

// --- Level badge ----------------------------------------------------------

export function LevelBadge({ level }: { level: string }) {
  return <span className="level-badge">{level}</span>;
}

// --- Bar (used by every "share of" list) ----------------------------------

export function Bar({
  share,
  color = 'var(--chart-1)',
}: {
  share: number;
  color?: string;
}) {
  return (
    <div className="bar">
      <div
        className="bar__fill"
        style={{ width: `${Math.max(0, Math.min(100, share))}%`, background: color }}
      />
    </div>
  );
}

// --- Empty / loading / error ----------------------------------------------

export function Empty({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="state">
      <div className="state__title">{title}</div>
      {detail && <div className="state__detail">{detail}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <div className="state__detail">{label}</div>
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: { message: string; status?: number };
  onRetry?: () => void;
}) {
  const forbidden = error.status === 403;
  return (
    <div className="state state--error" role="alert">
      <div className="state__title">
        {forbidden ? 'Outside your scope' : 'That did not load'}
      </div>
      <div className="state__detail">{error.message}</div>
      {onRetry && !forbidden && (
        <Button size="sm" onClick={onRetry} className="state__action">
          Try again
        </Button>
      )}
    </div>
  );
}

// --- Field ----------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <div className="field__error">{error}</div>
      ) : hint ? (
        <div className="field__hint">{hint}</div>
      ) : null}
    </div>
  );
}

// --- Segmented control ----------------------------------------------------

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string; count?: number }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`segmented__btn ${o.value === value ? 'is-active' : ''}`}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className="segmented__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

// --- Definition list ------------------------------------------------------

export function DefList({
  items,
}: {
  items: Array<{ term: string; value: ReactNode }>;
}) {
  return (
    <dl className="deflist">
      {items.map((i) => (
        <div className="deflist__row" key={i.term}>
          <dt>{i.term}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// --- Icons (inline stroke SVG, sized to the text) -------------------------

type IconProps = { size?: number; className?: string };

const svg = (path: ReactNode, { size = 16, className = '' }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {path}
  </svg>
);

export const Icon = {
  dashboard: (p: IconProps = {}) =>
    svg(
      <>
        <rect x="2" y="2" width="5.2" height="5.2" rx="1.2" />
        <rect x="8.8" y="2" width="5.2" height="5.2" rx="1.2" />
        <rect x="2" y="8.8" width="5.2" height="5.2" rx="1.2" />
        <rect x="8.8" y="8.8" width="5.2" height="5.2" rx="1.2" />
      </>,
      p,
    ),
  applications: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M3.4 2.2h6l3.2 3.2v8.4a.8.8 0 0 1-.8.8H3.4a.8.8 0 0 1-.8-.8V3a.8.8 0 0 1 .8-.8z" />
        <path d="M9.2 2.4v3.2h3.2M5.2 9h5.6M5.2 11.4h3.6" />
      </>,
      p,
    ),
  underwriting: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M8 1.7 13.4 4v4.2c0 2.9-2.2 5.2-5.4 6-3.2-.8-5.4-3.1-5.4-6V4z" />
        <path d="m5.6 8.2 1.7 1.7 3.2-3.4" />
      </>,
      p,
    ),
  merchants: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M2.4 6.2 3.6 2.6h8.8l1.2 3.6" />
        <path d="M3.2 6.2v7.2h9.6V6.2" />
        <path d="M6.4 13.4V9.4h3.2v4" />
      </>,
      p,
    ),
  equipment: (p: IconProps = {}) =>
    svg(
      <>
        <rect x="3.2" y="1.8" width="9.6" height="12.4" rx="1.6" />
        <path d="M5.6 4.6h4.8M5.6 7.4h1.6M9 7.4h1.4M5.6 10h1.6M9 10h1.4" />
      </>,
      p,
    ),
  supplies: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M2.6 4.4c0-1 1.2-1.8 2.6-1.8s2.6.8 2.6 1.8v7c0 1 1.2 1.8 2.6 1.8s2.6-.8 2.6-1.8V6" />
        <ellipse cx="5.2" cy="4.4" rx="2.6" ry="1.8" />
        <path d="M8 13.2H4.6" />
      </>,
      p,
    ),
  reports: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M2.4 13.4h11.2" />
        <path d="M4.4 11V7M7.4 11V3.6M10.4 11V8.6M13 11V5.4" />
      </>,
      p,
    ),
  org: (p: IconProps = {}) =>
    svg(
      <>
        <rect x="6" y="1.8" width="4" height="3.2" rx="0.8" />
        <rect x="1.8" y="11" width="4" height="3.2" rx="0.8" />
        <rect x="10.2" y="11" width="4" height="3.2" rx="0.8" />
        <path d="M8 5v3.2M3.8 11V8.2h8.4V11" />
      </>,
      p,
    ),
  permissions: (p: IconProps = {}) =>
    svg(
      <>
        <rect x="2.6" y="7" width="10.8" height="7.2" rx="1.6" />
        <path d="M5.3 7V4.9a2.7 2.7 0 0 1 5.4 0V7" />
      </>,
      p,
    ),
  search: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="7.2" cy="7.2" r="4.6" />
        <path d="m10.6 10.6 2.8 2.8" />
      </>,
      p,
    ),
  bell: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M4 6.6a4 4 0 0 1 8 0c0 3 1.2 4 1.2 4H2.8s1.2-1 1.2-4z" />
        <path d="M6.6 13a1.6 1.6 0 0 0 2.8 0" />
      </>,
      p,
    ),
  plus: (p: IconProps = {}) => svg(<path d="M8 3.4v9.2M3.4 8h9.2" />, p),
  minus: (p: IconProps = {}) => svg(<path d="M3.4 8h9.2" />, p),
  chevronRight: (p: IconProps = {}) => svg(<path d="m6.4 3.2 4.8 4.8-4.8 4.8" />, p),
  chevronDown: (p: IconProps = {}) => svg(<path d="m3.2 6.4 4.8 4.8 4.8-4.8" />, p),
  chevronLeft: (p: IconProps = {}) => svg(<path d="M9.6 3.2 4.8 8l4.8 4.8" />, p),
  check: (p: IconProps = {}) => svg(<path d="m3 8.4 3.2 3.2L13 5" />, p),
  alert: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="8" cy="8" r="6.3" />
        <path d="M8 4.8v4M8 11.1h.01" />
      </>,
      p,
    ),
  clock: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="8" cy="8" r="6.3" />
        <path d="M8 4.6v3.6l2.4 1.5" />
      </>,
      p,
    ),
  download: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M8 2.4v7.2M5 6.8 8 9.8l3-3" />
        <path d="M2.8 11.6v1.2a.8.8 0 0 0 .8.8h8.8a.8.8 0 0 0 .8-.8v-1.2" />
      </>,
      p,
    ),
  sun: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="8" cy="8" r="3.2" />
        <path d="M8 1.4v1.6M8 13v1.6M1.4 8h1.6M13 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
      </>,
      p,
    ),
  moon: (p: IconProps = {}) =>
    svg(<path d="M13.4 9.4A5.8 5.8 0 0 1 6.2 2.4a5.9 5.9 0 1 0 7.2 7z" />, p),
  /** A display, for the theme that follows whatever the device is doing. */
  display: (p: IconProps = {}) =>
    svg(
      <>
        <rect x="1.6" y="2.4" width="12.8" height="8.8" rx="1.4" />
        <path d="M5.6 14h4.8M8 11.2V14" />
      </>,
      p,
    ),
  trash: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M2.6 4.2h10.8M6.2 4.2V2.8h3.6v1.4M3.9 4.2l.6 9a1 1 0 0 0 1 .9h5a1 1 0 0 0 1-.9l.6-9" />
        <path d="M6.6 7v4M9.4 7v4" />
      </>,
      p,
    ),
  settings: (p: IconProps = {}) =>
    svg(
      <>
        <circle cx="8" cy="8" r="2.4" />
        <path d="M8 1.6v1.8M8 12.6v1.8M1.6 8h1.8M12.6 8h1.8M3.5 3.5l1.3 1.3M11.2 11.2l1.3 1.3M12.5 3.5l-1.3 1.3M4.8 11.2l-1.3 1.3" />
      </>,
      p,
    ),
  close: (p: IconProps = {}) =>
    svg(<path d="M3.8 3.8l8.4 8.4M12.2 3.8l-8.4 8.4" />, p),
  logout: (p: IconProps = {}) =>
    svg(
      <>
        <path d="M6 13.4H3.4a.8.8 0 0 1-.8-.8V3.4a.8.8 0 0 1 .8-.8H6" />
        <path d="M10 11.2 13.2 8 10 4.8M13.2 8H6" />
      </>,
      p,
    ),
  logo: ({ size = 17 }: { size?: number } = {}) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.2 12.2 8 3l4.8 9.2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="M5.7 9.4h4.6" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
};
